"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useSetSeats = void 0;
const react_1 = __importDefault(require("react"));
const seats_1 = require("@app/request/organization/seats");
const expected_errors_1 = require("@app/request/auth/expected-errors");
const maybe_unwrap_1 = require("@app/util/apollo/maybe-unwrap");
const util_1 = require("@app/util");
// long enough that typing "120" asks once, short enough that the price still feels attached
const PREVIEW_DEBOUNCE_MS = 400;
const errorText = (errors) => {
    const error = errors?.[0];
    if (!(0, util_1.isValue)(error))
        return undefined;
    if ((0, expected_errors_1.matchesExpected)(error, [seats_1.SEATS_EXPECTED_ERRORS.BELOW_EMPLOYEE_COUNT])) {
        return (0, maybe_unwrap_1.unwrapMaybe)((0, util_1.t)('INVOICING.seatsErrorBelowEmployees'));
    }
    if ((0, expected_errors_1.matchesExpected)(error, [seats_1.SEATS_EXPECTED_ERRORS.MANAGED_IN_TRIAL])) {
        return (0, maybe_unwrap_1.unwrapMaybe)((0, util_1.t)('INVOICING.seatsErrorTrial'));
    }
    if ((0, expected_errors_1.matchesExpected)(error, [seats_1.SEATS_EXPECTED_ERRORS.PAYMENT_FAILED])) {
        // the message carries Stripe's own reason, worth showing as is
        return (0, maybe_unwrap_1.unwrapMaybe)((0, util_1.t)('INVOICING.seatsErrorPaymentFailed', { message: error.error.message ?? '' }));
    }
    // anything else was already toasted by the request
    return undefined;
};
/**
 * The price preview and the save behind the seats modal. Owns the backend error codes so the
 * modal only renders `error`.
 */
const useSetSeats = ({ organizationId, seats, isValid }) => {
    const [preview, setPreview] = react_1.default.useState(undefined);
    const [isPreviewLoading, setIsPreviewLoading] = react_1.default.useState(false);
    const [isSaving, setIsSaving] = react_1.default.useState(false);
    const [error, setError] = react_1.default.useState(undefined);
    react_1.default.useEffect(() => {
        setPreview(undefined);
        setError(undefined);
        if (!(0, util_1.isValue)(organizationId) || !(0, util_1.isValue)(seats) || !isValid) {
            setIsPreviewLoading(false);
            return;
        }
        setIsPreviewLoading(true);
        let isCurrent = true;
        const timeout = setTimeout(() => {
            void (0, seats_1.requestOrganizationSeatsPreview)({ organization: organizationId, seats }).then((result) => {
                if (!isCurrent)
                    return;
                setPreview(result);
                setIsPreviewLoading(false);
            });
        }, PREVIEW_DEBOUNCE_MS);
        return () => {
            isCurrent = false;
            clearTimeout(timeout);
        };
    }, [organizationId, seats, isValid]);
    const save = react_1.default.useCallback(async () => {
        if (!(0, util_1.isValue)(organizationId) || !(0, util_1.isValue)(seats))
            return false;
        setIsSaving(true);
        setError(undefined);
        let errors;
        const result = await (0, seats_1.requestOrganizationSetSeats)({ organization: organizationId, seats }, (err) => {
            errors = err;
        });
        setIsSaving(false);
        if (!(0, util_1.isValue)(result)) {
            setError(errorText(errors));
            return false;
        }
        return true;
    }, [organizationId, seats]);
    return { preview, isPreviewLoading, isSaving, error, save };
};
exports.useSetSeats = useSetSeats;
exports.default = exports.useSetSeats;
